import { motion, useScroll, useTransform } from "framer-motion";
import { useRef } from "react";
import { useLang } from "@/context/LanguageContext";
import { useContent } from "@/context/ContentContext";
import { Reveal, Overline } from "@/components/Reveal";
import { CITY } from "@/assets";

const Stat = ({ value, label, delay }) => (
  <Reveal delay={delay} className="border-t border-white/15 pt-4">
    <div className="font-display text-5xl md:text-6xl text-purple-400">{value}</div>
    <div className="text-sm text-white/50 mt-1 uppercase tracking-wider">{label}</div>
  </Reveal>
);

export const About = () => {
  const { t, lang } = useLang();
  const { content } = useContent();
  const bio = (content.bios && content.bios[lang]) || {};
  const title = content.name || t.about.title;
  const lead = bio.lead || t.about.lead;
  const body = bio.body || t.about.body;
  const s1 = content.stat1 || "15";
  const s2 = content.stat2 || "4K+";
  const s3 = content.stat3 || "30";
  const ref = useRef(null);
  const { scrollYProgress } = useScroll({ target: ref, offset: ["start end", "end start"] });
  const clip = useTransform(scrollYProgress, [0, 0.6], ["inset(100% 0% 0% 0%)", "inset(0% 0% 0% 0%)"]);
  const y = useTransform(scrollYProgress, [0, 1], [-40, 40]);

  return (
    <section id="about" data-testid="about-section" className="relative py-24 md:py-40 max-w-7xl mx-auto px-6 md:px-12">
      <div className="grid lg:grid-cols-12 gap-12 lg:gap-16 items-start">
        <div className="lg:col-span-5 order-2 lg:order-1" ref={ref}>
          <motion.div style={{ clipPath: clip }} className="relative overflow-hidden rounded-sm">
            <motion.img style={{ y, scale: 1.1 }} src={CITY} alt="Davenport" className="w-full h-[440px] md:h-[560px] object-cover" />
            <div className="absolute inset-0 ring-1 ring-inset ring-white/10" />
          </motion.div>
        </div>

        <div className="lg:col-span-7 order-1 lg:order-2">
          <Reveal><Overline>{t.about.overline}</Overline></Reveal>
          <Reveal delay={0.1}>
            <h2 className="font-display uppercase text-5xl sm:text-7xl leading-[0.9] mt-4">{title}</h2>
          </Reveal>
          <Reveal delay={0.2}>
            <p className="text-xl md:text-2xl font-light text-white/85 mt-8 leading-snug">{lead}</p>
          </Reveal>
          <Reveal delay={0.3}>
            <div className="mt-6 max-w-xl space-y-4">
              {body.split("\n").map((para, i) => para.trim() && (
                <p key={i} className="text-base text-white/55 leading-relaxed">{para.trim()}</p>
              ))}
            </div>
          </Reveal>

          <div className="grid grid-cols-3 gap-6 mt-14">
            <Stat value={s1} label={t.about.stat1} delay={0.1} />
            <Stat value={s2} label={t.about.stat2} delay={0.2} />
            <Stat value={s3} label={t.about.stat3} delay={0.3} />
          </div>
        </div>
      </div>
    </section>
  );
};
